import { useAuthTokenProvider } from "@jugl-web/domain-resources/auth/providers/AuthTokenProvider";
import type {
  ChatBotSettingsFormValues,
  ChatBotUIConversationMessage,
  ChatBotUIMessage,
  ChatBotUIRef,
  ChatBotUISendMessagePayload,
  ChatBotUIVoiceMode,
} from "@jugl-web/domain-resources/chat-bot";
import { ChatBotUI } from "@jugl-web/domain-resources/chat-bot";
import {
  useAgentImageSource,
  useChatImageSource,
} from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import { useVoiceModeFatalErrorAlert } from "@jugl-web/domain-resources/chat-bot/hooks/useVoiceModeFatalErrorAlert";
import { useVoiceModeSession } from "@jugl-web/domain-resources/chat-bot/hooks/useVoiceModeSession";
import type { VoiceModeFatalErrorReason } from "@jugl-web/domain-resources/chat-bot/services/VoiceModeSession";
import {
  buildChatBotUIMessagePayload,
  transformChatBotUIMessagePayloadToBackendResponsePayload,
} from "@jugl-web/domain-resources/chat-bot/utils/chatBotUITransformers";
import { isVoiceModeSupported as isVoiceModeSupportedFn } from "@jugl-web/domain-resources/chat-bot/utils/isVoiceModeSupported";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { resolveChatMessagePayloadToText } from "@jugl-web/rest-api/integrations/models/ChatBotMessage";
import { cx, getUniqueId } from "@jugl-web/utils";
import { requestMicrophoneAccess } from "@jugl-web/utils/audio/requestMicrophoneAccess";
import { environment } from "@web-src/environments/environment";
import { createPlaygroundMessagesStoreHook } from "@web-src/modules/cx/utils/createPlaygroundMesagesStoreHook";
import {
  FC,
  useCallback,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from "react";
import { BackdropContainer } from "./components/BackdropContainer";
import { ButtonPositionIndicators } from "./components/ButtonPositionIndicators";
import { CTAPreview } from "./components/CTAPreview";
import { ChatBotButton } from "./components/ChatBotButton";
import {
  BUTTON_SIZE,
  INITIAL_INCOMING_MESSAGE,
  MOCKED_OUTGOING_MESSAGE,
  PREVIEW_USER_NAME,
  WELCOME_MESSAGE_ID,
} from "./consts";

export type ChatBotChannelPreviewMode =
  | "default"
  | "launcher"
  | "ctas"
  | "suggestions"
  | "chat-only";

export interface ChatbotChannelPreviewProps extends ChatBotSettingsFormValues {
  entityId: string;
  isPreview?: boolean;
  chatMode?: "active" | "mocked";
  previewMode?: ChatBotChannelPreviewMode;
  onRevise?: (params: { question: string; agentAnswer: string }) => void;
}

const useChatBotChannelPreviewMessagesStore =
  createPlaygroundMessagesStoreHook<ChatBotUIConversationMessage>([
    INITIAL_INCOMING_MESSAGE,
  ]);

const isVoiceModeSupported = isVoiceModeSupportedFn();

export const ChatBotChannelPreview: FC<ChatbotChannelPreviewProps> = ({
  entityId,
  chatName,
  agentName,
  welcomeMessage,
  senderMessageBackgroundColor,
  theme,
  buttonColor,
  alignment,
  bottomSpacing,
  sideSpacing,
  chatImage,
  agentImage,
  buttonIcon,
  ctas,
  ctaVisual,
  suggestions,
  voice_config,
  chatMode = "active",
  previewMode = "default",
  isPreview,
  onRevise,
}) => {
  const { aiWorkflowsApi } = useRestApiProvider();
  const { getToken } = useAuthTokenProvider();

  const [sendAgentChatMessage, { isLoading: isSendAgentChatMessageLoading }] =
    aiWorkflowsApi.useSendAgentChatMessageMutation();

  /**
   * Defer the form-driven props so that typing in any input field does
   * not force an immediate re-render of the potentially-large message list.
   */
  const deferredAgentName = useDeferredValue(agentName);
  const deferredAgentImage = useDeferredValue(agentImage);
  const deferredWelcomeMessage = useDeferredValue(welcomeMessage);

  const chatImageSrc = useChatImageSource(chatImage);
  const agentImageSrc = useAgentImageSource(deferredAgentImage);

  const activeChatMessagesSource =
    useChatBotChannelPreviewMessagesStore(entityId);

  const mockedChatMessagesSource = useMemo(() => {
    return {
      messages: [INITIAL_INCOMING_MESSAGE, MOCKED_OUTGOING_MESSAGE],
      setMessages: () => {},
    };
  }, []);

  const { messages, setMessages } =
    chatMode === "active" ? activeChatMessagesSource : mockedChatMessagesSource;

  const chatBotUIRef = useRef<ChatBotUIRef>(null);
  const { getVoiceModeFatalErrorAlert } = useVoiceModeFatalErrorAlert();

  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const isVoiceModeAvailable = voice_config.enabled && isVoiceModeSupported;

  const jwtToken = getToken() as string;

  const handleVoiceModeCallEnded = useCallback(() => {
    setIsVoiceMode(false);
    chatBotUIRef.current?.enqueueAlert({
      content: "Voice chat ended",
      variant: "info",
    });
  }, []);

  const handleVoiceModeError = useCallback((message: string) => {
    chatBotUIRef.current?.enqueueAlert({
      content: message,
      variant: "error",
    });
  }, []);

  const handleVoiceModeFatalError = useCallback(
    (reason: VoiceModeFatalErrorReason) => {
      setIsVoiceMode(false);
      chatBotUIRef.current?.enqueueAlert(getVoiceModeFatalErrorAlert(reason));
    },
    [getVoiceModeFatalErrorAlert]
  );

  const handleSetVoiceMode = useCallback(async (isActive: boolean) => {
    if (!isActive) {
      setIsVoiceMode(false);
      return;
    }

    const microphoneAccess = await requestMicrophoneAccess();

    if (microphoneAccess !== "granted") {
      chatBotUIRef.current?.enqueueAlert({
        content:
          microphoneAccess === "denied"
            ? "Microphone access is required for voice mode"
            : "Microphone is unavailable on this device",
        variant: "error",
      });
      return;
    }

    setIsVoiceMode(true);
  }, []);

  const { session: voiceModeSession, snapshot: voiceModeSnapshot } =
    useVoiceModeSession({
      url: `wss://${environment.wsDomain}/voice`,
      params: {
        token: jwtToken,
        entity_id: entityId,
        playground: "true",
        web: "true",
      },
      onEndCall: handleVoiceModeCallEnded,
      onError: handleVoiceModeError,
      onFatalError: handleVoiceModeFatalError,
      skip: !isVoiceModeAvailable || !isVoiceMode,
    });

  const voiceModeUIConfig = useMemo<ChatBotUIVoiceMode | undefined>(() => {
    if (!isVoiceMode || !voiceModeSession) {
      return undefined;
    }

    return {
      waveformSamples: voiceModeSnapshot.waveformSamples,
      isConnecting:
        voiceModeSnapshot.micStatus === "requesting" ||
        voiceModeSnapshot.transportStatus === "connecting",
      isMuted: voiceModeSnapshot.isMuted,
      isAgentSpeaking: voiceModeSnapshot.playbackStatus === "playing",
      onExit: handleVoiceModeCallEnded,
      onMuteToggle: () => voiceModeSession.toggleMute(),
    };
  }, [
    handleVoiceModeCallEnded,
    isVoiceMode,
    voiceModeSession,
    voiceModeSnapshot,
  ]);

  /** Reset to a fresh welcome message; display values are re-applied by displayMessages. */
  const resetMessages = useCallback(async () => {
    if (chatMode === "mocked") {
      return;
    }

    setIsResetting(true);

    const response = await sendAgentChatMessage({
      entityId,
      // Agent will reset the context once we type "reset" in the chat
      input: "reset",
      medium: "chatbot",
      agent_id: entityId,
      preview: isPreview || undefined,
    });

    if ("data" in response) {
      setMessages([INITIAL_INCOMING_MESSAGE]);
    }

    setIsResetting(false);
  }, [chatMode, entityId, isPreview, sendAgentChatMessage, setMessages]);

  const filteredSuggestions = useMemo(
    () => suggestions.map((s) => s.value.trim()).filter((v) => v.length > 0),
    [suggestions]
  );

  const hasConversationJustStarted = useMemo(
    () =>
      messages.length === 0 ||
      (messages.length === 1 && messages[0].id === WELCOME_MESSAGE_ID),
    [messages]
  );

  const handleSendMessage = useCallback(
    async (payload: ChatBotUISendMessagePayload) => {
      if (chatMode === "mocked") {
        return;
      }

      const backendResponsePayload =
        transformChatBotUIMessagePayloadToBackendResponsePayload(payload);

      const agentInput = backendResponsePayload.text;
      const plainText = resolveChatMessagePayloadToText(backendResponsePayload);

      const optimisticId = getUniqueId();

      const optimisticMessage: ChatBotUIMessage = {
        id: optimisticId,
        type: "outgoing",
        timestamp: new Date(),
        payload: {
          type: "text",
          text: plainText,
          quickReplies: [],
        },
        senderName: PREVIEW_USER_NAME,
        senderImageUrl: "",
      };

      setMessages((prev) => [...prev, optimisticMessage]);

      window.setTimeout(() => {
        chatBotUIRef.current?.scrollToLastMessage();
      }, 100);
      try {
        const resp = await sendAgentChatMessage({
          entityId,
          input: agentInput,
          medium: "chatbot",
          agent_id: entityId,
          preview: isPreview || undefined,
        }).unwrap();

        const agentMessage: ChatBotUIMessage = {
          id: getUniqueId(),
          type: "incoming",
          timestamp: new Date(),
          payload: buildChatBotUIMessagePayload({
            plainText: resp.agent_response,
            quickReplies: resp.quick_replies ?? [],
            ctaToolResult: resp.cta_tool_result,
          }),
          senderName: agentName || "Support",
          senderImageUrl: agentImageSrc,
        };

        setMessages((prev) => [...prev, agentMessage]);

        window.setTimeout(() => {
          chatBotUIRef.current?.scrollToLastMessage();
        }, 100);
      } catch {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      }
    },
    [
      chatMode,
      setMessages,
      sendAgentChatMessage,
      entityId,
      isPreview,
      agentName,
      agentImageSrc,
    ]
  );

  const displayMessages = useMemo(
    () =>
      messages.map<ChatBotUIConversationMessage>((message) => {
        if (message.type === "outgoing") {
          return message;
        }

        return {
          ...message,
          senderName: deferredAgentName,
          senderImageUrl: agentImageSrc,
          ...(message.id === "welcome"
            ? {
                payload: {
                  ...message.payload,
                  text: deferredWelcomeMessage,
                },
              }
            : {}),
        };
      }),
    [messages, deferredAgentName, agentImageSrc, deferredWelcomeMessage]
  );

  return (
    <div className="flex h-full w-full justify-center py-6">
      {/* Inner container */}
      <BackdropContainer
        alignment={alignment}
        isBackdropVisible={previewMode === "launcher" || previewMode === "ctas"}
        className={cx(
          "my-auto",
          previewMode === "ctas" && "h-auto min-h-[300px] justify-end"
        )}
      >
        {/* Chatbot Frame */}
        {previewMode !== "ctas" && (
          <div className="h-full w-full max-w-[410px] overflow-hidden rounded-3xl shadow-[0_8px_24px_rgba(0,0,0,0.15)]">
            <ChatBotUI
              ref={chatBotUIRef}
              messages={displayMessages}
              suggestions={
                hasConversationJustStarted || previewMode === "suggestions"
                  ? filteredSuggestions
                  : undefined
              }
              config={{
                headerTitle: chatName,
                userBubbleColor: senderMessageBackgroundColor,
                agentName: deferredAgentName,
                agentImageUrl: agentImageSrc,
                chatImageUrl: chatImageSrc,
              }}
              isInitialLoading={isResetting}
              isTyping={isSendAgentChatMessageLoading}
              isInputDisabled={chatMode === "mocked"}
              isSending={isSendAgentChatMessageLoading}
              voiceMode={voiceModeUIConfig}
              onSendMessage={handleSendMessage}
              onSetVoiceMode={
                isVoiceModeAvailable ? handleSetVoiceMode : undefined
              }
              onRestart={chatMode === "active" ? resetMessages : undefined}
              getMessageActions={(message, index, allMessages) => {
                if (message.type !== "incoming" || !onRevise) return undefined;

                const prev = allMessages[index - 1];

                if (prev?.type === "outgoing" && prev.payload.type === "text") {
                  const question = prev.payload.text;
                  const agentAnswer = message.payload.text;

                  return {
                    onRevise: () =>
                      onRevise({
                        question,
                        agentAnswer,
                      }),
                  };
                }

                return undefined;
              }}
            />
          </div>
        )}
        {previewMode === "ctas" && (
          <CTAPreview
            ctas={ctas}
            ctaVisual={ctaVisual}
            alignment={alignment}
            buttonSize={BUTTON_SIZE}
          />
        )}
        {/* Launcher button */}
        {previewMode !== "chat-only" && (
          <ChatBotButton
            isOpen={false}
            icon={buttonIcon}
            buttonColor={buttonColor}
            className={cx(
              "z-10",
              alignment === "right" ? "self-end" : "self-start"
            )}
          />
        )}
        {/* Vectors */}
        {previewMode === "launcher" && (
          <ButtonPositionIndicators
            alignment={alignment}
            bottomSpacing={bottomSpacing}
            sideSpacing={sideSpacing}
          />
        )}
      </BackdropContainer>
    </div>
  );
};
